import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Every mounted useCredits instance, so that a refetch in one updates all.
 *
 * WHY THIS EXISTS (2026-08-19)
 * ----------------------------
 * useCredits used to watch `user_credits` over Realtime, and that is how the
 * header balance was meant to stay current. `user_credits` is not in the
 * supabase_realtime publication (measured against pg_publication_tables:
 * only audit_logs and payment_processing_log are), so the handler could
 * never fire. The hook is called independently by eight components, so the
 * app was opening eight dead channels per session.
 *
 * Removing them exposed the real defect underneath. Each call to useCredits
 * owns its own useState, so `refetch` only ever updated the instance it came
 * from. ClaimGift and CoinsPurchaseSuccess both call it, and neither could
 * reach the header. Realtime was the only thing that would have crossed
 * instances, and it was dead, so the header balance had NO working update
 * path at all: it was whatever it read on mount until a full reload.
 *
 * A registry rather than a context because the hook's signature does not
 * change and no provider has to be threaded through the tree. If credits
 * grow more shared state than a single number, promote this to a context.
 */
const listeners = new Set<() => void>();

export const useCredits = () => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  /** True once a read has actually succeeded. `balance` is seeded to 0, so
   *  without this a caller cannot tell "no coins" from "not read yet" or
   *  "the read failed" -- and all three produce the same number. */
  const [authoritative, setAuthoritative] = useState(false);

  // Ref to avoid recreating fetchBalance on every token refresh
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const userId = user?.id;

  const fetchBalance = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser) {
      setBalance(0);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_credits')
        .select('balance')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (error) throw error;
      setBalance(data?.balance ?? 0);
      setAuthoritative(true);
    } catch (_error) {
      // Keep the last known balance to avoid flickering, but do NOT claim it is
      // authoritative. A failed read used to be indistinguishable from a real
      // zero, and a zero balance is what forces Free mode -- so a network blip
      // silently downgraded a coin holder. useGenerationLimits:63-71 is the
      // same codebase getting this right with an explicit flag.
      setAuthoritative(false);
    } finally {
      setLoading(false);
    }
  }, []); // Stable — reads user from ref

  useEffect(() => {
    fetchBalance();
  }, [userId, fetchBalance]);

  // Register for cross-instance refreshes. fetchBalance is stable (useCallback
  // with an empty dep list, reading the user from a ref), so this adds and
  // removes exactly once per mount rather than churning the set.
  useEffect(() => {
    listeners.add(fetchBalance);
    return () => { listeners.delete(fetchBalance); };
  }, [fetchBalance]);

  /**
   * Refetch this instance, then every other mounted one.
   *
   * Awaiting resolves when THIS instance is current, which is what existing
   * callers depend on (ClaimGift awaits it before showing the new balance).
   * The others are refreshed without being awaited, because the caller has
   * no interest in the header finishing before it continues.
   */
  const refetch = useCallback(async () => {
    await fetchBalance();
    for (const listener of listeners) {
      if (listener !== fetchBalance) listener();
    }
  }, [fetchBalance]);

  return { balance, loading, authoritative, refetch };
};
